/**
 * OCTOPUS - SignalsConfig (Phase 999.4 Plan 03, split do LiveTelemetryConfig — D-03)
 *
 * Aba "Telemetria (Ao vivo)" do diálogo Editar Equipamento: SÓ visibilidade
 * de sinais (`live_visible` por path) — quais sinais detectados aparecem na
 * página "Ao vivo". Cadastro físico dos motores (papel/posição/regimes/modelo)
 * é responsabilidade do `MotorsConfig` (aba "Motores").
 *
 * Payload PARCIAL (não-destrutivo): o handler `PUT /equipments/{id}/live-config`
 * (backend/routes/equipment.py) trata `motors`/`remove_motors` e `signals` em
 * blocos `if` INDEPENDENTES — `LiveConfigUpdate` tem defaults `[]` para os 3
 * campos, então omitir `motors`/`remove_motors` do payload NUNCA apaga o
 * cadastro de motores (confirmado lendo o handler antes do split, Pitfall A4
 * do plano). Este componente só envia `{ signals }`.
 *
 * Autônomo: busca seu próprio GET /equipments/{id}/live-view ao montar (o
 * Radix Tabs desmonta o conteúdo inativo — o fetch só acontece quando a aba
 * "Telemetria (Ao vivo)" está aberta).
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { showErrorToast } from '@/utils/errorHandler';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SignalsConfig = ({ equipmentId }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [groups, setGroups] = useState(null);
  const [openGroups, setOpenGroups] = useState({}); // {groupKey: bool}
  const [signalEdits, setSignalEdits] = useState({}); // {path: bool}

  const fetchConfig = useCallback(async () => {
    if (!equipmentId) return;
    try {
      setLoading(true);
      const res = await axios.get(
        `${API}/equipments/${equipmentId}/live-view`,
        { params: { include_hidden: true } }
      );
      setGroups(res.data?.groups || []);
      setSignalEdits({});
    } catch (error) {
      console.error('Erro ao carregar sinais de telemetria:', error);
      showErrorToast(toast, error, 'Erro ao carregar telemetria');
    } finally {
      setLoading(false);
    }
  }, [equipmentId]);

  useEffect(() => {
    if (equipmentId) fetchConfig();
  }, [equipmentId, fetchConfig]);

  const effectiveVisible = (source) => signalEdits[source.path] ?? source.live_visible;

  const dirty = Object.keys(signalEdits).length > 0;

  const handleSave = async () => {
    try {
      setSaving(true);
      const signals = Object.entries(signalEdits).map(([path, live_visible]) => ({ path, live_visible }));
      // PARCIAL: sem `motors`/`remove_motors` no payload — LiveConfigUpdate tem
      // default [] p/ os dois e o handler só toca o cadastro de motores
      // `if update.motors or update.remove_motors:` (nunca apaga motores).
      await axios.put(`${API}/equipments/${equipmentId}/live-config`, { signals });
      toast.success('Visibilidade de sinais salva');
      await fetchConfig();
    } catch (error) {
      console.error('Erro ao salvar sinais de telemetria:', error);
      showErrorToast(toast, error, 'Erro ao salvar telemetria');
    } finally {
      setSaving(false);
    }
  };

  // Switch mestre: liga/desliga TODOS os sinais do grupo de uma vez
  const setGroupVisibility = (group, visible) => {
    setSignalEdits(prev => {
      const next = { ...prev };
      for (const source of group.sources) {
        next[source.path] = visible;
      }
      return next;
    });
  };

  const renderSignalRow = (source) => {
    const nameIsPath = source.display_name === source.path;
    return (
      <div key={source.path} className="flex items-center justify-between gap-2 py-1.5 overflow-hidden">
        <div className="min-w-0 flex-1">
          {!nameIsPath && <p className="text-sm truncate">{source.display_name}</p>}
          <p className={`text-xs text-muted-foreground font-mono truncate ${nameIsPath ? 'text-sm' : ''}`}>
            {source.path}
          </p>
        </div>
        <Switch
          className="shrink-0"
          checked={effectiveVisible(source)}
          onCheckedChange={(checked) => setSignalEdits(prev => ({ ...prev, [source.path]: checked }))}
          data-testid={`live-signal-${source.path}`}
        />
      </div>
    );
  };

  const renderGroup = (group) => {
    const isOpen = !!openGroups[group.key];
    const visibleCount = group.sources.filter(effectiveVisible).length;
    const total = group.sources.length;
    const allVisible = visibleCount === total && total > 0;

    return (
      <div key={group.key} className="rounded-lg border border-border overflow-hidden">
        {/* Header do grupo: expandir + nome + contador + switch mestre */}
        <div className="flex items-center gap-2 p-3 bg-muted/30">
          <button
            type="button"
            className="flex items-center gap-2 min-w-0 flex-1 text-left"
            onClick={() => setOpenGroups(prev => ({ ...prev, [group.key]: !isOpen }))}
            data-testid={`live-group-toggle-${group.key}`}
          >
            {isOpen ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
            <span className="text-sm font-medium truncate">{group.label}</span>
            <span className="text-xs text-muted-foreground shrink-0">
              {visibleCount}/{total} visíveis
            </span>
          </button>
          <Switch
            className="shrink-0"
            checked={allVisible}
            onCheckedChange={(checked) => setGroupVisibility(group, checked)}
            title={allVisible ? 'Desligar todos os sinais da seção' : 'Ligar todos os sinais da seção'}
            data-testid={`live-group-all-${group.key}`}
          />
        </div>

        {/* Lista de sinais (colapsada por default) */}
        {isOpen && (
          <div className="px-3 pb-2 divide-y divide-border/50 border-t border-border/50">
            {group.sources.map(renderSignalRow)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-3" data-testid="signals-config">
      <p className="text-xs text-muted-foreground">
        Quais sinais detectados aparecem na página "Ao vivo". O switch do
        cabeçalho liga/desliga a seção inteira; expanda para ajustar sinal a
        sinal. Cadastro físico dos motores (papel/posição/regimes) fica na
        aba "Motores".
      </p>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Carregando sinais...
        </div>
      )}

      {!loading && groups !== null && groups.length === 0 && (
        <p className="text-sm text-muted-foreground py-2">
          Nenhum sinal detectado ainda — o cadastro fica disponível quando o
          equipamento enviar telemetria.
        </p>
      )}

      {!loading && (groups || []).map(renderGroup)}

      {!loading && groups !== null && groups.length > 0 && (
        <div className="flex justify-end">
          <Button
            type="button"
            size="sm"
            onClick={handleSave}
            disabled={!dirty || saving}
            data-testid="signals-save-btn"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Salvar telemetria
          </Button>
        </div>
      )}
    </div>
  );
};

export default SignalsConfig;
